import { BasicModal, Typography } from "@/components";
import { useBoolean } from "@/hooks/useBoolean";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

interface DashboardMenuProps {
	cases?: number;
	todo?: number;
	chats?: number;
}

const DashboardMenu = ({
	cases = 0,
	todo = 0,
	chats = 0,
}: DashboardMenuProps) => {
	const { t } = useTranslation(["dashboard", "common"]);
	const navigate = useNavigate();
	const {
		value: isOpenConfirmConsent,
		setTrue: openConfirmConsent,
		setFalse: closeConfirmConsent,
	} = useBoolean();
	const {
		value: isOpenHemiDialog,
		setTrue: openHemiDialog,
		setFalse: closeHemiDialog,
	} = useBoolean();
	const onCloseModal = () => {
		closeConfirmConsent();
	};

	const onClickConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		closeConfirmConsent();
		return navigate({
			to: "/dashboard/case/referrals",
		});
	};

	const onClickHemiConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		closeHemiDialog();
		window.location.href = import.meta.env.VITE_HEMI_URL;
	};
	return (
		<>
			<Link
				to="/dashboard/case/active"
				className="mx-auto py-4 md:p-4 flex flex-col justify-center items-center gap-2"
			>
				<div className="relative w-fit bg-brand rounded-full shadow-sm">
					<Badge badgeContent={cases} color="error" overlap="circular">
						<svg
							width="57"
							height="57"
							viewBox="0 0 57 57"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<title>{t("dashboard:menu.casesMdt")}</title>
							<g clipPath="url(#clip0_6278_934)">
								<path
									d="M35.0839 38.75H32.4589C31.8149 38.75 31.2917 38.2285 31.2917 37.5828C31.2917 36.9388 31.8132 36.4155 32.4589 36.4155H35.0839C35.7279 36.4155 36.2512 35.8923 36.2512 35.2483V26.4983C36.2512 25.8543 36.7727 25.331 37.4184 25.331C38.0642 25.331 38.5857 25.8543 38.5857 26.4983V35.2483C38.5839 37.1803 37.0142 38.75 35.0839 38.75Z"
									fill="#36A9FA"
								/>
								<path
									d="M40.6244 14.25H34.2072C33.0819 14.25 32.1667 15.1652 32.1667 16.2922V21.8345C32.1667 24.729 34.5222 27.0845 37.4167 27.0845C40.3112 27.0845 42.6667 24.729 42.6667 21.8345V16.2922C42.6667 15.1652 41.7514 14.25 40.6244 14.25ZM39.4589 20.9578H38.2917V22.125C38.2917 22.608 37.8997 23 37.4167 23C36.9337 23 36.5417 22.608 36.5417 22.125V20.9578H35.3744C34.8914 20.9578 34.4994 20.5658 34.4994 20.0828C34.4994 19.5998 34.8914 19.2078 35.3744 19.2078H36.5417V18.0405C36.5417 17.5575 36.9337 17.1655 37.4167 17.1655C37.8997 17.1655 38.2917 17.5575 38.2917 18.0405V19.2078H39.4589C39.9419 19.2078 40.3339 19.5998 40.3339 20.0828C40.3339 20.5658 39.9419 20.9578 39.4589 20.9578Z"
									fill="#36A9FA"
								/>
								<path
									d="M32.4589 42.25H15.5417C15.0587 42.25 14.6667 41.858 14.6667 41.375V37.2923C14.6667 34.8808 16.6302 32.9173 19.0417 32.9173H28.9589C31.3704 32.9173 33.3339 34.8808 33.3339 37.2923V41.375C33.3339 41.858 32.9419 42.25 32.4589 42.25Z"
									fill="#0158A3"
								/>
								<path
									d="M18.8667 24.2827C18.8667 24.26 18.8667 24.225 18.8789 24.2005C19.3917 21.8432 21.4917 20.0827 24.0012 20.0827C26.5212 20.0827 28.6334 21.8677 29.1339 24.2355C29.1339 24.2477 29.1462 24.2705 29.1462 24.2827C29.1339 24.2827 29.1112 24.295 29.0989 24.295C27.1389 24.82 25.8667 22.685 25.8212 22.5922C25.7284 22.44 25.5762 22.335 25.4012 22.3122C25.2262 22.2895 25.0512 22.335 24.9112 22.4645C22.4367 24.7622 19.1344 24.3177 18.8667 24.2827Z"
									fill="#0158A3"
								/>
								<path
									d="M28.4566 25.5427C26.8816 25.5427 25.7616 24.4455 25.2016 23.7455C22.5416 25.8332 19.4039 25.5305 18.7616 25.4482C18.7494 25.6232 18.7616 25.786 18.7844 25.9505C19.0994 28.5527 21.3166 30.5828 23.9994 30.5828C26.6944 30.5828 28.9221 28.5405 29.2144 25.9155C29.2371 25.7633 29.2494 25.6127 29.2371 25.4482C28.9694 25.52 28.7139 25.5427 28.4566 25.5427Z"
									fill="#0158A3"
								/>
							</g>
							<defs>
								<clipPath id="clip0_6278_934">
									<rect
										width="56"
										height="56"
										fill="white"
										transform="translate(0.666656 0.25)"
									/>
								</clipPath>
							</defs>
						</svg>
					</Badge>
				</div>
				<Typography fontWeight={400} fontSize={14} align="center">
					{t("dashboard:menu.casesMdt")}
				</Typography>
			</Link>
			<Link
				to="/dashboard/case/records"
				className="mx-auto py-4 md:p-4 flex flex-col justify-center items-center gap-2"
			>
				<div className="relative w-fit bg-brand rounded-full shadow-sm">
					<svg
						width="56"
						height="57"
						viewBox="0 0 56 57"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<title>{t("dashboard:menu.record")}</title>
						<g clipPath="url(#clip0_6278_948)">
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M17.5 17.5266C17.5 15.717 18.967 14.25 20.7765 14.25H35.0746C36.8841 14.25 38.3511 15.717 38.3511 17.5266V38.9735C38.3511 40.783 36.8841 42.25 35.0746 42.25H20.7765C18.967 42.25 17.5 40.783 17.5 38.9735V17.5266ZM20.7765 16.0372C19.954 16.0372 19.2873 16.704 19.2873 17.5266V38.9735C19.2873 39.796 19.954 40.4627 20.7765 40.4627H35.0746C35.8971 40.4627 36.5638 39.796 36.5638 38.9735V17.5266C36.5638 16.704 35.8971 16.0372 35.0746 16.0372H20.7765Z"
								fill="#36A9FA"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M26.1383 16.633C25.6448 16.633 25.2448 17.033 25.2448 17.5266V18.4203H24.3511C23.8576 18.4203 23.4575 18.8203 23.4575 19.3138V22.8884C23.4575 23.3819 23.8576 23.7819 24.3511 23.7819H25.2448V24.6755C25.2448 25.1691 25.6448 25.5692 26.1383 25.5692H29.7129C30.2064 25.5692 30.6064 25.1691 30.6064 24.6755V23.7819H31.5C31.9937 23.7819 32.3937 23.3819 32.3937 22.8884V19.3138C32.3937 18.8203 31.9937 18.4203 31.5 18.4203H30.6064V17.5266C30.6064 17.033 30.2064 16.633 29.7129 16.633H26.1383Z"
								fill="#0158A3"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M22.2659 28.25C22.2659 27.7565 22.666 27.3564 23.1595 27.3564H32.6914C33.1851 27.3564 33.5851 27.7565 33.5851 28.25C33.5851 28.7435 33.1851 29.1437 32.6914 29.1437H23.1595C22.666 29.1437 22.2659 28.7435 22.2659 28.25Z"
								fill="#36A9FA"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M22.2659 31.2288C22.2659 30.7352 22.666 30.3351 23.1595 30.3351H32.6914C33.1851 30.3351 33.5851 30.7352 33.5851 31.2288C33.5851 31.7223 33.1851 32.1224 32.6914 32.1224H23.1595C22.666 32.1224 22.2659 31.7223 22.2659 31.2288Z"
								fill="#36A9FA"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M22.2659 34.8031C22.2659 34.3096 22.666 33.9095 23.1595 33.9095H32.6914C33.1851 33.9095 33.5851 34.3096 33.5851 34.8031C33.5851 35.2967 33.1851 35.6968 32.6914 35.6968H23.1595C22.666 35.6968 22.2659 35.2967 22.2659 34.8031Z"
								fill="#36A9FA"
							/>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M22.2659 38.3776C22.2659 37.8841 22.666 37.484 23.1595 37.484H27.9254C28.4189 37.484 28.8192 37.8841 28.8192 38.3776C28.8192 38.8713 28.4189 39.2713 27.9254 39.2713H23.1595C22.666 39.2713 22.2659 38.8713 22.2659 38.3776Z"
								fill="#36A9FA"
							/>
						</g>
						<defs>
							<clipPath id="clip0_6278_948">
								<rect
									width="56"
									height="56"
									fill="white"
									transform="translate(0 0.25)"
								/>
							</clipPath>
						</defs>
					</svg>
				</div>
				<Typography fontWeight={400} fontSize={14} align="center">
					{t("dashboard:menu.record")}
				</Typography>
			</Link>
			<Link
				to="/dashboard/case/progress-notes"
				className="mx-auto py-4 md:p-4 flex flex-col justify-center items-center gap-2"
			>
				<div className="relative w-fit bg-brand rounded-full shadow-sm">
					<svg
						width="57"
						height="57"
						viewBox="0 0 57 57"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<title>{t("dashboard:menu.progressNotes")}</title>
						<g clipPath="url(#clip0_6385_3281)">
							<path
								d="M30.9595 16.5828H29.6767C29.29 15.2423 28.0545 14.25 26.5845 14.25C25.1145 14.25 23.8772 15.2423 23.4922 16.5828H22.2095C21.7317 16.5828 21.3345 16.98 21.3345 17.4578V19.2078C21.3345 20.3278 22.2567 21.25 23.3767 21.25H29.7922C30.9122 21.25 31.8345 20.3278 31.8345 19.2078V17.4578C31.8345 16.98 31.4372 16.5828 30.9595 16.5828Z"
								fill="#36A9FA"
							/>
							<path
								d="M33.2916 17.75H32.9994V19.2078C32.9994 20.9805 31.5644 22.4155 29.7916 22.4155H23.3744C21.6016 22.4155 20.1666 20.9805 20.1666 19.2078V17.75H19.8744C18.1016 17.75 16.6666 19.185 16.6666 20.9578V35.5405C16.6666 37.315 18.1016 38.75 19.8744 38.75H28.4844L28.7416 37.315C28.8589 36.6623 29.1616 36.0778 29.6289 35.6L30.5616 34.6672H21.0416C20.5639 34.6672 20.1666 34.27 20.1666 33.7922C20.1666 33.3145 20.5639 32.9172 21.0416 32.9172H32.1244C32.1821 32.9172 32.2294 32.9173 32.2871 32.94H32.2994L36.4994 28.74V20.9578C36.4994 19.185 35.0644 17.75 33.2916 17.75ZM32.1244 30.875H21.0416C20.5639 30.875 20.1666 30.4777 20.1666 30C20.1666 29.5223 20.5639 29.125 21.0416 29.125H32.1244C32.6021 29.125 32.9994 29.5223 32.9994 30C32.9994 30.4777 32.6039 30.875 32.1244 30.875ZM32.1244 27.0828H21.0416C20.5639 27.0828 20.1666 26.6855 20.1666 26.2078C20.1666 25.73 20.5639 25.3328 21.0416 25.3328H32.1244C32.6021 25.3328 32.9994 25.73 32.9994 26.2078C32.9994 26.6873 32.6039 27.0828 32.1244 27.0828Z"
								fill="#0158A3"
							/>
							<path
								d="M31.2804 42.2496C31.0494 42.2496 30.8271 42.1586 30.6609 41.9941C30.4596 41.7928 30.3686 41.5041 30.4176 41.2241L31.0371 37.7171C31.0686 37.5403 31.1526 37.3776 31.2804 37.2498L39.9429 28.5873C41.0069 27.5216 42.0516 27.8086 42.6239 28.3808L44.0676 29.8246C44.8639 30.6208 44.8639 31.9158 44.0676 32.7121L35.4051 41.3746C35.2791 41.5023 35.1164 41.5863 34.9379 41.6178L31.4326 42.2373C31.3819 42.2443 31.3311 42.2496 31.2804 42.2496Z"
								fill="#36A9FA"
							/>
						</g>
						<defs>
							<clipPath id="clip0_6385_3281">
								<rect
									width="56"
									height="56"
									fill="white"
									transform="translate(0.333313 0.25)"
								/>
							</clipPath>
						</defs>
					</svg>
				</div>
				<Typography fontWeight={400} fontSize={14} align="center">
					{t("dashboard:menu.progressNotes")}
				</Typography>
			</Link>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
			<div
				onClick={openConfirmConsent}
				className="mx-auto py-4 md:p-4 flex flex-col justify-center items-center gap-2 cursor-pointer"
			>
				<div className="relative w-fit bg-brand rounded-full shadow-sm">
					<svg
						width="57"
						height="57"
						viewBox="0 0 57 57"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<title>{t("dashboard:menu.referrals")}</title>
						<g clipPath="url(#clip0_6278_971)">
							<path
								d="M38.2917 27.5828C37.8087 27.5828 37.4167 27.1908 37.4167 26.7078V21.4578C37.4167 20.6545 36.7622 20 35.9589 20H27.2089C26.7259 20 26.3339 19.608 26.3339 19.125C26.3339 18.642 26.7259 18.25 27.2089 18.25H35.9589C37.7282 18.25 39.1667 19.6885 39.1667 21.4578V26.7078C39.1667 27.1908 38.7747 27.5828 38.2917 27.5828Z"
								fill="#36A9FA"
							/>
							<path
								d="M38.2917 27.5827C38.0677 27.5827 37.8437 27.497 37.6722 27.3272L35.6317 25.285C35.3814 25.0347 35.3062 24.6585 35.4427 24.3312C35.5774 24.004 35.8977 23.7905 36.2512 23.7905H40.3339C40.6874 23.7905 41.0059 24.004 41.1424 24.3312C41.2772 24.6585 41.2037 25.0347 40.9534 25.285L38.9112 27.3272C38.7397 27.4987 38.5157 27.5827 38.2917 27.5827Z"
								fill="#36A9FA"
							/>
							<path
								d="M27.7917 39.25H21.3744C19.6052 39.25 18.1667 37.8115 18.1667 36.0423V30.7923C18.1667 30.3093 18.5587 29.9173 19.0417 29.9173C19.5247 29.9173 19.9167 30.3093 19.9167 30.7923V36.0423C19.9167 36.8455 20.5712 37.5 21.3744 37.5H27.7917C28.2747 37.5 28.6667 37.892 28.6667 38.375C28.6667 38.858 28.2747 39.25 27.7917 39.25Z"
								fill="#36A9FA"
							/>
							<path
								d="M21.0839 33.7077H16.9994C16.6459 33.7077 16.3274 33.4942 16.1909 33.167C16.0544 32.8397 16.1314 32.4652 16.3817 32.215L18.4239 30.1727C18.7652 29.8315 19.3199 29.8315 19.6612 30.1727L21.7034 32.215C21.9537 32.4652 22.0289 32.8415 21.8924 33.1687C21.7559 33.496 21.4374 33.7077 21.0839 33.7077Z"
								fill="#36A9FA"
							/>
							<path
								d="M20.4994 20.5845C22.1106 20.5845 23.4167 19.2784 23.4167 17.6672C23.4167 16.0561 22.1106 14.75 20.4994 14.75C18.8883 14.75 17.5822 16.0561 17.5822 17.6672C17.5822 19.2784 18.8883 20.5845 20.4994 20.5845Z"
								fill="#0158A3"
							/>
							<path
								d="M23.1244 21.75H17.8744C16.1052 21.75 14.6667 23.1885 14.6667 24.9578V25.5405C14.6667 26.0235 15.0587 26.4155 15.5417 26.4155H25.4589C25.9419 26.4155 26.3339 26.0235 26.3339 25.5405V24.9578C26.3339 23.1885 24.8937 21.75 23.1244 21.75Z"
								fill="#0158A3"
							/>
							<path
								d="M36.8339 36.9172C35.2257 36.9172 33.9167 35.6082 33.9167 34C33.9167 32.3917 35.2257 31.0827 36.8339 31.0827C38.4422 31.0827 39.7512 32.3917 39.7512 34C39.7494 35.6082 38.4422 36.9172 36.8339 36.9172Z"
								fill="#0158A3"
							/>
							<path
								d="M41.7917 42.75H31.8744C31.3914 42.75 30.9994 42.358 30.9994 41.875V41.2923C30.9994 39.523 32.4379 38.0845 34.2072 38.0845H39.4572C41.2264 38.0845 42.6649 39.523 42.6649 41.2923V41.875C42.6667 42.358 42.2747 42.75 41.7917 42.75Z"
								fill="#0158A3"
							/>
						</g>
						<defs>
							<clipPath id="clip0_6278_971">
								<rect
									width="56"
									height="56"
									fill="white"
									transform="translate(0.666656 0.75)"
								/>
							</clipPath>
						</defs>
					</svg>
				</div>
				<Typography fontWeight={400} fontSize={14} align="center">
					{t("dashboard:menu.referrals")}
				</Typography>
			</div>
			<Link
				to="/dashboard/inbox/reminders"
				className="mx-auto py-4 md:p-4 flex flex-col justify-center items-center gap-2"
			>
				<div className="relative w-fit bg-brand rounded-full shadow-sm">
					<Badge badgeContent={todo} color="error" overlap="circular">
						<svg
							width="56"
							height="57"
							viewBox="0 0 56 57"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<title>{t("dashboard:menu.toDoList")}</title>
							<g clipPath="url(#clip0_6278_986)">
								<path
									d="M36.225 14.75H19.775C18.515 14.75 17.5 15.765 17.5 17.025V40.475C17.5 41.735 18.515 42.75 19.775 42.75H36.225C37.485 42.75 38.5 41.735 38.5 40.475V17.025C38.5 15.765 37.485 14.75 36.225 14.75ZM25.6672 39.25C25.6672 39.8923 25.1423 40.4172 24.5 40.4172H21C20.3577 40.4172 19.8328 39.8923 19.8328 39.25V35.75C19.8328 35.1077 20.3577 34.5828 21 34.5828H24.5C25.1423 34.5828 25.6672 35.1077 25.6672 35.75V39.25ZM25.6672 31.0828C25.6672 31.725 25.1423 32.25 24.5 32.25H21C20.3577 32.25 19.8328 31.725 19.8328 31.0828V27.5828C19.8328 26.9405 20.3577 26.4155 21 26.4155H24.5C25.1423 26.4155 25.6672 26.9405 25.6672 27.5828V31.0828ZM25.6672 22.9172C25.6672 23.5595 25.1423 24.0845 24.5 24.0845H21C20.3577 24.0845 19.8328 23.5595 19.8328 22.9172V19.4172C19.8328 18.775 20.3577 18.25 21 18.25H24.5C25.1423 18.25 25.6672 18.775 25.6672 19.4172V22.9172ZM33.8328 38.0828H28C27.3577 38.0828 26.8328 37.5578 26.8328 36.9155C26.8328 36.275 27.3577 35.75 28 35.75H33.8328C34.475 35.75 35 36.275 35 36.9172C35 37.5577 34.475 38.0828 33.8328 38.0828ZM33.8328 29.9172H28C27.3577 29.9172 26.8328 29.3923 26.8328 28.75C26.8328 28.1077 27.3577 27.5828 28 27.5828H33.8328C34.475 27.5828 35 28.1077 35 28.75C35 29.3923 34.475 29.9172 33.8328 29.9172ZM33.8328 21.75H28C27.3577 21.75 26.8328 21.225 26.8328 20.5828C26.8328 19.9405 27.3577 19.4155 28 19.4155H33.8328C34.475 19.4155 35 19.9405 35 20.5828C35 21.225 34.475 21.75 33.8328 21.75Z"
									fill="#0158A3"
								/>
							</g>
							<defs>
								<clipPath id="clip0_6278_986">
									<rect
										width="56"
										height="56"
										fill="white"
										transform="translate(0 0.75)"
									/>
								</clipPath>
							</defs>
						</svg>
					</Badge>
				</div>
				<Typography fontWeight={400} fontSize={14} align="center">
					{t("dashboard:menu.toDoList")}
				</Typography>
			</Link>
			<Link
				to="/dashboard/calendar"
				className="mx-auto py-4 md:p-4 flex flex-col justify-center items-center gap-2"
			>
				<div className="relative w-fit bg-brand rounded-full shadow-sm">
					<svg
						width="57"
						height="57"
						viewBox="0 0 57 57"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<title>{t("dashboard:menu.calendar")}</title>
						<g clipPath="url(#clip0_6278_995)">
							<path
								d="M21.6255 27.5828H18.7083C18.2255 27.5828 17.8333 27.9745 17.8333 28.4578V31.375C17.8333 31.8578 18.2255 32.25 18.7083 32.25H21.6255C22.1088 32.25 22.5005 31.8578 22.5005 31.375V28.4578C22.5005 27.9745 22.1088 27.5828 21.6255 27.5828Z"
								fill="#36A9FA"
							/>
							<path
								d="M21.6255 34.5828H18.7083C18.2255 34.5828 17.8333 34.9745 17.8333 35.4578V38.375C17.8333 38.8578 18.2255 39.25 18.7083 39.25H21.6255C22.1088 39.25 22.5005 38.8578 22.5005 38.375V35.4578C22.5005 34.9745 22.1088 34.5828 21.6255 34.5828Z"
								fill="#36A9FA"
							/>
							<path
								d="M29.7911 27.5828H26.8738C26.3906 27.5828 25.9988 27.9745 25.9988 28.4578V31.375C25.9988 31.8578 26.3906 32.25 26.8738 32.25H29.7911C30.2739 32.25 30.6661 31.8578 30.6661 31.375V28.4578C30.6661 27.9745 30.2739 27.5828 29.7911 27.5828Z"
								fill="#36A9FA"
							/>
							<path
								d="M29.7911 34.5828H26.8738C26.3906 34.5828 25.9988 34.9745 25.9988 35.4578V38.375C25.9988 38.8578 26.3906 39.25 26.8738 39.25H29.7911C30.2739 39.25 30.6661 38.8578 30.6661 38.375V35.4578C30.6661 34.9745 30.2739 34.5828 29.7911 34.5828Z"
								fill="#0158A3"
							/>
							<path
								d="M37.9583 27.5828H35.0411C34.5579 27.5828 34.1661 27.9745 34.1661 28.4578V31.375C34.1661 31.8578 34.5579 32.25 35.0411 32.25H37.9583C38.4411 32.25 38.8333 31.8578 38.8333 31.375V28.4578C38.8333 27.9745 38.4411 27.5828 37.9583 27.5828Z"
								fill="#36A9FA"
							/>
							<path
								d="M38.8333 18.25H17.8333C15.903 18.25 14.3333 19.8197 14.3333 21.75V39.25C14.3333 41.1803 15.903 42.75 17.8333 42.75H38.8333C40.7636 42.75 42.3333 41.1803 42.3333 39.25V21.75C42.3333 19.8197 40.7636 18.25 38.8333 18.25ZM40.0005 39.25C40.0005 39.8939 39.4772 40.4172 38.8333 40.4172H17.8333C17.1895 40.4172 16.6661 39.8939 16.6661 39.25V26.463H39.9988V39.25H40.0005Z"
								fill="#0158A3"
							/>
							<path
								d="M21.3333 14.75H20.1661C19.5222 14.75 18.9988 15.2717 18.9988 15.9172V20.5845C18.9988 21.2283 19.5205 21.7517 20.1661 21.7517H21.3333C21.9772 21.7517 22.5005 21.23 22.5005 20.5845V15.9172C22.5005 15.2717 21.9772 14.75 21.3333 14.75Z"
								fill="#36A9FA"
							/>
							<path
								d="M36.5005 14.75H35.3333C34.6895 14.75 34.1661 15.2717 34.1661 15.9172V20.5845C34.1661 21.2283 34.6895 21.75 35.3333 21.75H36.5005C37.1444 21.75 37.6678 21.2283 37.6678 20.5828V15.9172C37.6661 15.2717 37.1444 14.75 36.5005 14.75Z"
								fill="#36A9FA"
							/>
						</g>
						<defs>
							<clipPath id="clip0_6278_995">
								<rect
									width="56"
									height="56"
									fill="white"
									transform="translate(0.333313 0.75)"
								/>
							</clipPath>
						</defs>
					</svg>
				</div>
				<Typography fontWeight={400} fontSize={14} align="center">
					{t("dashboard:menu.calendar")}
				</Typography>
			</Link>
			<Link
				to="/dashboard/contacts/directory"
				className="mx-auto py-4 md:p-4 flex flex-col justify-center items-center gap-2"
			>
				<div className="relative w-fit bg-brand rounded-full shadow-sm">
					<svg
						width="57"
						height="57"
						viewBox="0 0 57 57"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<title>{t("dashboard:menu.directory")}</title>
						<g clipPath="url(#clip0_6278_1011)">
							<path
								d="M33.9166 14.25C29.7359 14.25 26.3339 17.652 26.3339 21.8328C26.3339 26.0135 29.7359 29.4155 33.9166 29.4155C38.0974 29.4155 41.4994 26.0135 41.4994 21.8328C41.4994 17.652 38.0974 14.25 33.9166 14.25ZM38.9391 18.9172H36.8339V16.812C37.7071 17.3212 38.4299 18.044 38.9391 18.9172ZM39.7494 21.8345C39.7494 22.2335 39.7091 22.6238 39.6321 23.0018H36.8339V20.669H39.6321C39.7091 21.0435 39.7494 21.4337 39.7494 21.8345ZM28.0839 21.8345C28.0839 21.4355 28.1241 21.0452 28.2011 20.6672H30.9994V23H28.2011C28.1241 22.6237 28.0839 22.2335 28.0839 21.8345ZM32.7494 20.6672H35.0821V23H32.7494V20.6672ZM35.0839 16.119V18.9172H32.7511V16.119C33.1274 16.042 33.5176 16 33.9166 16C34.3156 16 34.7059 16.042 35.0839 16.119ZM30.9994 16.812V18.9172H28.8941C29.4051 18.044 30.1261 17.3212 30.9994 16.812ZM28.8941 24.75H30.9994V26.8553C30.1261 26.346 29.4051 25.6233 28.8941 24.75ZM32.7494 27.55V24.75H35.0821V27.55C34.7059 27.627 34.3156 27.6672 33.9149 27.6672C33.5176 27.6672 33.1274 27.627 32.7494 27.55ZM36.8339 26.8553V24.75H38.9391C38.4299 25.6233 37.7071 26.346 36.8339 26.8553Z"
								fill="#36A9FA"
							/>
							<path
								d="M36.8339 31.3195C35.9117 31.5995 34.9317 31.75 33.9167 31.75C28.4444 31.75 23.9994 27.305 23.9994 21.8327C23.9994 18.8 25.3644 16.07 27.5222 14.25H19.3322C17.4072 14.25 15.8322 15.825 15.8322 17.75V34.6672C15.8322 35.81 17.3949 37.5845 19.3322 37.5845H22.8322V41.375C22.8322 42.1362 23.7404 42.53 24.2899 42.0277L26.3322 40.2077L28.3744 42.0277C28.9064 42.5212 29.8322 42.1485 29.8322 41.375V37.5827H35.6667C36.3089 37.5827 36.8339 37.0577 36.8339 36.4155C36.8339 32.7597 36.8339 37.147 36.8339 31.3195ZM22.8339 35.25H19.3339C18.6917 35.25 18.1667 34.725 18.1667 34.0845C18.1667 33.4422 18.6917 32.9172 19.3339 32.9172H22.8339V35.25ZM34.4994 35.25H29.8322V32.9172H34.4994V35.25Z"
								fill="#0158A3"
							/>
						</g>
						<defs>
							<clipPath id="clip0_6278_1011">
								<rect
									width="56"
									height="56"
									fill="white"
									transform="translate(0.666656 0.25)"
								/>
							</clipPath>
						</defs>
					</svg>
				</div>
				<Typography fontWeight={400} fontSize={14} align="center">
					{t("dashboard:menu.directory")}
				</Typography>
			</Link>
			<Link
				to="/dashboard/contacts/contact"
				className="mx-auto py-4 md:p-4 flex flex-col justify-center items-center gap-2"
			>
				<div className="relative w-fit bg-brand rounded-full shadow-sm">
					<svg
						width="56"
						height="57"
						viewBox="0 0 56 57"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<title>{t("dashboard:menu.contacts")}</title>
						<g clipPath="url(#clip0_6278_1019)">
							<path
								d="M38.5 18.3328H37.0422V15.125C37.0422 14.642 36.6502 14.25 36.1672 14.25H17.7922C16.1822 14.25 14.875 15.559 14.875 17.1672V39.0422C14.875 40.8115 16.2873 42.25 18.025 42.25H38.5C38.983 42.25 39.375 41.858 39.375 41.375V19.2078C39.375 18.7248 38.983 18.3328 38.5 18.3328ZM33.5422 36.7078C33.5422 37.1908 33.1502 37.5828 32.6672 37.5828H22.75C22.267 37.5828 21.875 37.1908 21.875 36.7078V34.9578C21.875 33.1885 23.3135 31.75 25.0828 31.75H30.3328C32.102 31.75 33.5405 33.1885 33.5405 34.9578L33.5422 36.7078ZM24.2078 27.0828C24.2078 25.1525 25.7775 23.5828 27.7078 23.5828C29.638 23.5828 31.2078 25.1525 31.2078 27.0828C31.2078 29.013 29.638 30.5828 27.7078 30.5828C25.7775 30.5828 24.2078 29.013 24.2078 27.0828ZM35.2922 18.3328H17.7922C17.1482 18.3328 16.625 17.8095 16.625 17.1655C16.625 16.5233 17.1482 16 17.7922 16H35.2922V18.3328Z"
								fill="#0158A3"
							/>
						</g>
						<defs>
							<clipPath id="clip0_6278_1019">
								<rect
									width="56"
									height="56"
									fill="white"
									transform="translate(0 0.25)"
								/>
							</clipPath>
						</defs>
					</svg>
				</div>
				<Typography fontWeight={400} fontSize={14} align="center">
					{t("dashboard:menu.contacts")}
				</Typography>
			</Link>
			<Link
				to="/dashboard/chat/single"
				className="mx-auto py-4 md:p-4 flex flex-col justify-center items-center gap-2"
			>
				<div className="relative w-fit bg-brand rounded-full shadow-sm">
					<Badge badgeContent={chats} color="error" overlap="circular">
						<svg
							width="57"
							height="57"
							viewBox="0 0 57 57"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<title>{t("dashboard:menu.chats")}</title>
							<g clipPath="url(#clip0_6278_1026)">
								<path
									d="M24.2506 17.4578C24.2506 16.2328 24.7056 15.1127 25.4528 14.25H17.5428C15.7683 14.25 14.3333 15.685 14.3333 17.4578V23.875C14.3333 25.6477 15.7683 27.0828 17.5411 27.0828H18.9988V29.7078C18.9988 30.0578 19.2088 30.3727 19.5238 30.5127C19.6411 30.56 19.7566 30.5828 19.8738 30.5828C20.0961 30.5828 20.3061 30.5005 20.4688 30.35L24.0038 27.0828H25.4511C24.7038 26.22 24.2488 25.1 24.2488 23.875L24.2506 17.4578Z"
									fill="#36A9FA"
								/>
								<path
									d="M19.0005 36.4155C20.2889 36.4155 21.3333 35.3711 21.3333 34.0828C21.3333 32.7944 20.2889 31.75 19.0005 31.75C17.7122 31.75 16.6678 32.7944 16.6678 34.0828C16.6678 35.3711 17.7122 36.4155 19.0005 36.4155Z"
									fill="#0158A3"
								/>
								<path
									d="M20.4583 37.5828H17.5411C15.7718 37.5828 14.3333 39.023 14.3333 40.7923V41.375C14.3333 41.858 14.7253 42.25 15.2083 42.25H22.7911C23.2741 42.25 23.6661 41.858 23.6661 41.375V40.7923C23.6661 39.023 22.2276 37.5828 20.4583 37.5828Z"
									fill="#0158A3"
								/>
								<path
									d="M37.6661 36.4155C38.9544 36.4155 39.9988 35.3711 39.9988 34.0828C39.9988 32.7944 38.9544 31.75 37.6661 31.75C36.3777 31.75 35.3333 32.7944 35.3333 34.0828C35.3333 35.3711 36.3777 36.4155 37.6661 36.4155Z"
									fill="#0158A3"
								/>
								<path
									d="M39.1255 37.5828H36.2083C34.439 37.5828 33.0005 39.0213 33.0005 40.7905V41.375C33.0005 41.858 33.3925 42.25 33.8755 42.25H41.4583C41.9413 42.25 42.3333 41.858 42.3333 41.375V40.7923C42.3333 39.023 40.8948 37.5828 39.1255 37.5828Z"
									fill="#0158A3"
								/>
								<path
									d="M36.791 30.5828C36.5758 30.5828 36.3623 30.5023 36.1978 30.3517L32.6576 27.0828H29.2083C27.439 27.0828 26.0005 25.6442 26.0005 23.875V17.4578C26.0005 15.6885 27.439 14.25 29.2083 14.25H39.1255C40.8948 14.25 42.3333 15.6885 42.3333 17.4578V23.875C42.3333 25.6442 40.8948 27.0828 39.1255 27.0828H37.6678V29.7078C37.6678 30.056 37.4613 30.3693 37.1445 30.5093C37.029 30.56 36.9101 30.5828 36.791 30.5828Z"
									fill="#36A9FA"
								/>
							</g>
							<defs>
								<clipPath id="clip0_6278_1026">
									<rect
										width="56"
										height="56"
										fill="white"
										transform="translate(0.333313 0.25)"
									/>
								</clipPath>
							</defs>
						</svg>
					</Badge>
				</div>
				<Typography fontWeight={400} fontSize={14} align="center">
					{t("dashboard:menu.chats")}
				</Typography>
			</Link>
			<Link
				to="/dashboard/gallery"
				className="mx-auto py-4 md:p-4 flex flex-col justify-center items-center gap-2"
			>
				<div className="relative w-fit bg-brand rounded-full shadow-sm">
					<svg
						width="57"
						height="57"
						viewBox="0 0 57 57"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<title>{t("dashboard:menu.gallery")}</title>
						<g clipPath="url(#clip0_6278_1412)">
							<path
								d="M21.9589 38.0827C20.0917 38.0827 18.4292 36.8875 17.8254 35.106L17.7852 34.9712C17.6434 34.4987 17.5822 34.1032 17.5822 33.706V25.7522L14.7542 35.2022C14.3902 36.5917 15.2197 38.0337 16.6109 38.417L34.6517 43.2487C34.8757 43.3082 35.1014 43.3345 35.3219 43.3345C36.4839 43.3345 37.5462 42.5645 37.8419 41.427L38.8919 38.0845L21.9589 38.0827Z"
								fill="#0158A3"
							/>
							<path
								d="M25.1667 25.8327C26.4529 25.8327 27.4994 24.7862 27.4994 23.5C27.4994 22.2137 26.4529 21.1672 25.1667 21.1672C23.8804 21.1672 22.8339 22.2137 22.8339 23.5C22.8339 24.7862 23.8804 25.8327 25.1667 25.8327Z"
								fill="#36A9FA"
							/>
							<path
								d="M39.7494 17.6672H22.2494C20.6412 17.6672 19.3322 18.9762 19.3322 20.5845V33.4172C19.3322 35.0255 20.6412 36.3345 22.2494 36.3345H39.7494C41.3577 36.3345 42.6667 35.0255 42.6667 33.4172V20.5845C42.6667 18.9745 41.3577 17.6672 39.7494 17.6672ZM22.2494 20H39.7494C40.0714 20 40.3322 20.2607 40.3322 20.5827V28.8655L36.6467 24.5657C36.2564 24.1072 35.6894 23.8622 35.0822 23.8482C34.4784 23.8517 33.9097 24.1195 33.5229 24.585L29.1917 29.786L27.7812 28.3755C26.9832 27.5775 25.6847 27.5775 24.8867 28.3755L21.6667 31.5955V20.5827C21.6667 20.2607 21.9274 20 22.2494 20Z"
								fill="#0158A3"
							/>
						</g>
						<defs>
							<clipPath id="clip0_6278_1412">
								<rect
									width="56"
									height="56"
									fill="white"
									transform="translate(0.666687 0.75)"
								/>
							</clipPath>
						</defs>
					</svg>
				</div>
				<Typography fontWeight={400} fontSize={14} align="center">
					{t("dashboard:menu.gallery")}
				</Typography>
			</Link>
			<button
				type="button"
				onClick={openHemiDialog}
				className="mx-auto py-4 md:p-4 flex flex-col justify-center items-center gap-2 bg-transparent border-none cursor-pointer"
			>
				<div className="relative w-[57px] h-[57px] rounded-full shadow-sm bg-brand p-2">
					<img
						src="/images/hemi-new.png"
						alt="hemi"
						className="object-cover w-full h-full"
					/>
				</div>
				<Typography fontWeight={400} fontSize={14} align="center">
					{t("dashboard:menu.hemi")}
				</Typography>
			</button>
			<Link
				to="/dashboard/bulletin"
				className="mx-auto py-4 md:p-4 flex flex-col justify-center items-center gap-2"
			>
				<div className="relative w-fit bg-brand rounded-full shadow-sm">
					<svg
						width="57"
						height="57"
						viewBox="0 0 57 57"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<title>{t("dashboard:menu.bulletins")}</title>
						<g clipPath="url(#clip0_6278_1067)">
							<path
								d="M23.9583 25.25H18.7083C18.2253 25.25 17.8333 24.858 17.8333 24.375V19.125C17.8333 18.642 18.2253 18.25 18.7083 18.25H23.9583C24.4413 18.25 24.8333 18.642 24.8333 19.125V24.375C24.8333 24.858 24.4413 25.25 23.9583 25.25Z"
								fill="#36A9FA"
							/>
							<path
								d="M30.9583 21.1672H28.0411C27.5581 21.1672 27.1661 20.7752 27.1661 20.2922C27.1661 19.8092 27.5581 19.4172 28.0411 19.4172H30.9583C31.4413 19.4172 31.8333 19.8092 31.8333 20.2922C31.8333 20.7752 31.4413 21.1672 30.9583 21.1672Z"
								fill="#36A9FA"
							/>
							<path
								d="M30.9583 24.6672H28.0411C27.5581 24.6672 27.1661 24.2752 27.1661 23.7922C27.1661 23.3092 27.5581 22.9172 28.0411 22.9172H30.9583C31.4413 22.9172 31.8333 23.3092 31.8333 23.7922C31.8333 24.2752 31.4413 24.6672 30.9583 24.6672Z"
								fill="#36A9FA"
							/>
							<path
								d="M30.9583 29.3328H18.7083C18.2253 29.3328 17.8333 28.9408 17.8333 28.4578C17.8333 27.9748 18.2253 27.5828 18.7083 27.5828H30.9583C31.4413 27.5828 31.8333 27.9748 31.8333 28.4578C31.8333 28.9408 31.4413 29.3328 30.9583 29.3328Z"
								fill="#36A9FA"
							/>
							<path
								d="M30.9583 33.7078H18.7083C18.2253 33.7078 17.8333 33.3158 17.8333 32.8328C17.8333 32.3498 18.2253 31.9578 18.7083 31.9578H30.9583C31.4413 31.9578 31.8333 32.3498 31.8333 32.8328C31.8333 33.3158 31.4413 33.7078 30.9583 33.7078Z"
								fill="#36A9FA"
							/>
							<path
								d="M30.9583 38.0828H18.7083C18.2253 38.0828 17.8333 37.6908 17.8333 37.2078C17.8333 36.7248 18.2253 36.3328 18.7083 36.3328H30.9583C31.4413 36.3328 31.8333 36.7248 31.8333 37.2078C31.8333 37.6908 31.4413 38.0828 30.9583 38.0828Z"
								fill="#36A9FA"
							/>
							<path
								d="M38.8333 18.25H36.5006V38.0828V40.09C35.8076 39.6857 35.3333 38.9403 35.3333 38.0828V18.25C35.3333 16.3197 33.7636 14.75 31.8333 14.75H17.8333C15.9031 14.75 14.3333 16.3197 14.3333 18.25V38.0828C14.3333 40.657 16.4263 42.75 19.0006 42.75H37.9583C38.0581 42.75 38.1543 42.7377 38.2471 42.715C40.5466 42.428 42.3333 40.4592 42.3333 38.0828V21.75C42.3333 19.8197 40.7636 18.25 38.8333 18.25ZM19.0006 40.4172C17.7143 40.4172 16.6678 39.3707 16.6678 38.0845V18.25C16.6678 17.606 17.1911 17.0828 17.8351 17.0828H31.8351C32.4791 17.0828 33.0023 17.606 33.0023 18.25V38.0828C33.0023 38.9315 33.2298 39.7295 33.6288 40.4155L19.0006 40.4172Z"
								fill="#0158A3"
							/>
						</g>
						<defs>
							<clipPath id="clip0_6278_1067">
								<rect
									width="56"
									height="56"
									fill="white"
									transform="translate(0.333313 0.75)"
								/>
							</clipPath>
						</defs>
					</svg>
				</div>
				<Typography fontWeight={400} fontSize={14} align="center">
					{t("dashboard:menu.bulletins")}
				</Typography>
			</Link>
			<BasicModal isOpen={isOpenConfirmConsent} onClose={onCloseModal}>
				<div className="flex flex-col gap-4">
					<Typography fontWeight={600} fontSize={16} align="center">
						{t("dashboard:menu.consentConfirmTitle")}
					</Typography>
					<Button
						fullWidth
						variant="contained"
						color="primary"
						size="large"
						className="!rounded-full"
						onClick={onClickConfirm}
					>
						{t("dashboard:menu.yes")}
					</Button>
					<Button
						fullWidth
						variant="outlined"
						color="primary"
						size="large"
						className="!rounded-full"
						onClick={onCloseModal}
					>
						{t("dashboard:menu.no")}
					</Button>
				</div>
			</BasicModal>
			<BasicModal isOpen={isOpenHemiDialog} onClose={closeHemiDialog}>
				<div className="flex flex-col gap-4">
					<Typography fontWeight={400} fontSize={14} align="center">
						{t("dashboard:menu.hemiRedirectMessage")}
					</Typography>
					<Button
						variant="contained"
						color="primary"
						size="large"
						className="!rounded-full"
						fullWidth
						onClick={onClickHemiConfirm}
					>
						{t("common:ok")}
					</Button>
					<Button
						fullWidth
						variant="outlined"
						color="primary"
						size="large"
						className="!rounded-full"
						onClick={closeHemiDialog}
					>
						{t("common:cancel")}
					</Button>
				</div>
			</BasicModal>
		</>
	);
};

export default DashboardMenu;
